import { useEffect, useRef, useState } from "react";
import { Star, PenLine, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVIEWS = [
  {
    text: "Dům jsme měli postavený za tři měsíce, přesně jak slíbili. Žádné navyšování rozpočtu, žádné výmluvy — prostě práce, na kterou je spolehnutí.",
    name: "Martin K.",
    place: "Praha-západ",
    date: "únor 2026",
    rating: 5,
  },
  {
    text: "Bytové jádro hotové za týden. Čistá práce, skvělá komunikace a všechno přesně podle domluvy. Konečně řemeslníci, které můžu doporučit dál.",
    name: "Jana H.",
    place: "Brno",
    date: "duben 2026",
    rating: 5,
  },
  {
    text: "Konečně někdo, kdo počítá materiál přesně a nevyhazuje. Díky kalkulaci předem jsme ušetřili desetitisíce už při nákupu materiálu.",
    name: "Lucie a Petr S.",
    place: "Liberec",
    date: "červen 2026",
    rating: 5,
  },
];

const PROJECT_TYPES = [
  "Novostavba domu",
  "Rekonstrukce domu",
  "Rekonstrukce bytu",
  "Bytové jádro",
  "Komerční fit-out",
  "Jiné",
];

const MONTHS_CS = [
  "leden", "únor", "březen", "duben", "květen", "červen",
  "červenec", "srpen", "září", "říjen", "listopad", "prosinec",
];

function formatDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${MONTHS_CS[d.getMonth()]} ${d.getFullYear()}`;
}

function Stars({ count }) {
  return (
    <div className="flex gap-1 text-gold">
      {[0, 1, 2, 3, 4].map((s) => (
        <Star
          key={s}
          size={15}
          fill={s < count ? "currentColor" : "none"}
          strokeWidth={s < count ? 0 : 1.5}
          className={s < count ? "" : "text-zinc-600"}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review, testid, delay }) {
  return (
    <Reveal delay={delay} className="h-full">
      <figure
        className="h-full bg-card2 border border-white/5 hover:border-gold/30 rounded-xl p-7 flex flex-col transition-colors duration-500"
        data-testid={testid}
      >
        <Stars count={review.rating} />
        <blockquote className="mt-5 text-zinc-300 leading-relaxed text-sm flex-1">
          „{review.text}"
        </blockquote>
        {review.photos && review.photos.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {review.photos.map((p, pi) => (
              <a
                key={p}
                href={`${API}/review-photo/${p}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`${testid}-photo-${pi}`}
              >
                <img
                  src={`${API}/review-photo/${p}`}
                  alt={`Fotka od ${review.name}`}
                  loading="lazy"
                  className="w-16 h-16 object-cover rounded-lg border border-white/10 hover:border-gold/50 transition-colors"
                />
              </a>
            ))}
          </div>
        )}
        <figcaption className="mt-6 pt-4 border-t border-white/5">
          <p className="text-zinc-100 font-semibold text-sm">{review.name}</p>
          <p className="text-zinc-500 text-xs mt-0.5">{review.place}</p>
          {review.date && (
            <p className="text-zinc-600 text-xs mt-0.5">{review.date}</p>
          )}
        </figcaption>
      </figure>
    </Reveal>
  );
}

export default function Reviews() {
  const [approved, setApproved] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [name, setName] = useState("");
  const [projectType, setProjectType] = useState(PROJECT_TYPES[0]);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [company, setCompany] = useState("");
  const [photos, setPhotos] = useState([]);
  const fileRef = useRef(null);

  const addPhotos = (files) => {
    setPhotos((prev) => [...prev, ...files].slice(0, 5));
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    fetch(`${API}/reviews`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setApproved(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [submitted]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const photoPaths = [];
      for (const f of photos) {
        const fd = new FormData();
        fd.append("file", f);
        const up = await fetch(`${API}/reviews/photos`, { method: "POST", body: fd });
        if (!up.ok) throw new Error();
        photoPaths.push((await up.json()).path);
      }
      const res = await fetch(`${API}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          text,
          rating,
          project_type: projectType,
          photos: photoPaths,
          company,
        }),
      });
      if (!res.ok) throw new Error();
      setSubmitted(true);
      toast.success("Recenze odeslána. Po schválení se zobrazí na webu.");
    } catch {
      toast.error("Odeslání se nezdařilo. Zkuste to prosím znovu později.");
    } finally {
      setSending(false);
    }
  };

  const live = approved.map((r) => ({
    text: r.text,
    name: r.name,
    place: r.project_type,
    date: formatDate(r.created_at),
    rating: r.rating,
    photos: r.photos || [],
  }));

  return (
    <section id="recenze" data-testid="reviews-section" className="py-24 lg:py-32 bg-coal/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-gold/70 mb-4">
            Reference
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-zinc-100 leading-tight">
            Co říkají <span className="gold-text">naši klienti</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {live.map((r, i) => (
            <ReviewCard key={`live-${r.name}-${i}`} review={r} testid={`review-live-card-${i}`} delay={i * 0.08} />
          ))}
          {REVIEWS.map((r, i) => (
            <ReviewCard key={r.name} review={r} testid={`review-card-${i}`} delay={(live.length + i) * 0.08} />
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 max-w-2xl mx-auto">
            {!formOpen && !submitted && (
              <div className="text-center">
                <p className="text-zinc-400 text-sm mb-5">
                  Dělali jsme pro vás? Budeme rádi, když se podělíte o zkušenost.
                </p>
                <button
                  type="button"
                  onClick={() => setFormOpen(true)}
                  data-testid="review-form-open-button"
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors duration-300 rounded-full px-7 py-3 text-sm font-semibold"
                >
                  <PenLine size={16} />
                  Napsat recenzi
                </button>
              </div>
            )}

            {formOpen && !submitted && (
              <form
                onSubmit={submitReview}
                data-testid="review-form"
                className="bg-card2 border border-white/5 rounded-xl p-7 space-y-5"
              >
                <p className="text-zinc-100 font-semibold text-sm">Vaše recenze</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    required
                    minLength={2}
                    maxLength={120}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Vaše jméno"
                    data-testid="review-form-name-input"
                    className="w-full bg-ink border border-white/10 focus:border-gold/50 rounded-lg px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-colors"
                  />
                  <select
                    value={projectType}
                    onChange={(e) => setProjectType(e.target.value)}
                    data-testid="review-form-type-select"
                    className="w-full bg-ink border border-white/10 focus:border-gold/50 rounded-lg px-4 py-3 text-sm text-zinc-100 outline-none transition-colors"
                  >
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 text-xs mr-2">Hodnocení:</span>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setRating(s)}
                      data-testid={`review-form-star-${s}`}
                      className="text-gold hover:scale-110 transition-transform"
                      aria-label={`${s} hvězdiček`}
                    >
                      <Star size={22} fill={s <= rating ? "currentColor" : "none"} strokeWidth={s <= rating ? 0 : 1.5} className={s <= rating ? "" : "text-zinc-600"} />
                    </button>
                  ))}
                </div>
                <textarea
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={4}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Jak probíhala spolupráce? Co jste ocenili?"
                  data-testid="review-form-text-input"
                  className="w-full bg-ink border border-white/10 focus:border-gold/50 rounded-lg px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-colors resize-y"
                />
                <div>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="review-form-photo-button"
                    className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 hover:border-gold/50 hover:text-gold transition-colors rounded-full px-5 py-2.5 text-sm"
                  >
                    <ImagePlus size={16} />
                    Přidat fotky z realizace (max 5)
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    multiple
                    hidden
                    data-testid="review-form-photo-input"
                    onChange={(e) => {
                      addPhotos([...e.target.files]);
                      e.target.value = "";
                    }}
                  />
                  {photos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3">
                      {photos.map((f, i) => (
                        <div key={i} className="relative" data-testid={`review-photo-preview-${i}`}>
                          <img
                            src={URL.createObjectURL(f)}
                            alt={`Náhled fotky ${i + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border border-white/10"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(i)}
                            data-testid={`review-photo-remove-${i}`}
                            className="absolute -top-2 -right-2 bg-ink border border-white/20 rounded-full p-1 text-zinc-400 hover:text-gold transition-colors"
                            aria-label={`Odebrat fotku ${i + 1}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="review-form-submit-button"
                    className="bg-gold text-black font-semibold text-sm rounded-full px-7 py-3 hover:brightness-110 transition disabled:opacity-50"
                  >
                    {sending ? "Odesílám…" : "Odeslat recenzi"}
                  </button>
                  <p className="text-zinc-600 text-xs">
                    Recenze se objeví po aktualizaci, nejpozději následující den.
                  </p>
                </div>
              </form>
            )}

            {submitted && (
              <div
                data-testid="review-form-success-message"
                className="bg-card2 border border-gold/30 rounded-xl p-7 text-center"
              >
                <p className="text-gold font-semibold">Děkujeme za recenzi!</p>
                <p className="text-zinc-400 text-sm mt-2">
                  Recenze se objeví na této stránce po aktualizaci, nejpozději následující den.
                </p>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
